import { prettySignLabel } from '../ZodiacClashHelpers';
import { useTranslation } from 'react-i18next';

export default function ClashResultNarrative({
  styles,
  revealResults,
  resultNarrative,
  predictionResult,
  day,
  winnerChoice,
  displayPctA,
  displayPctB,
  justVoted,
  totalVotes,
}) {
  const { t } = useTranslation('zodiacClash');
  if (!revealResults || !resultNarrative) return null;

  const hasWinner = winnerChoice === 'A' || winnerChoice === 'B';

  const winnerSign = hasWinner
    ? winnerChoice === 'A'
      ? day?.signA
      : day?.signB
    : null;

  const winnerPercent = hasWinner
    ? winnerChoice === 'A'
      ? Number(displayPctA || 0)
      : Number(displayPctB || 0)
    : 50;

  const selectedPercent =
    justVoted === 'A'
      ? Number(displayPctA || 0)
      : justVoted === 'B'
        ? Number(displayPctB || 0)
        : null;

  const matchedCommunity =
    hasWinner && justVoted === winnerChoice;

  const safeTotalVotes = Math.max(0, Number(totalVotes || 0));
  const isEarlyResult = safeTotalVotes > 0 && safeTotalVotes < 5;

  const winnerLabel = winnerSign
    ? prettySignLabel(winnerSign)
    : t('cosmicDraw');

  return (
    <div
      className={`zdxResultReveal${
        matchedCommunity ? ' zdxResultRevealHit' : ''
      }`}
      style={styles.resultNarrativeCard}
      aria-live="polite"
    >
      <div style={styles.resultNarrativeEyebrow}>
        {isEarlyResult
          ? t('initialResult')
          : hasWinner
            ? t('duelResult')
            : t('duelPulse')}
      </div>

      <div style={styles.resultWinnerTitle}>
        {hasWinner
          ? isEarlyResult
            ? t('leadsForNow', { sign: winnerLabel })
            : t('winner', { sign: winnerLabel })
          : t('clashDraw')}
      </div>

      <div style={styles.resultPercentRow}>
        {!isEarlyResult && (
          <strong style={styles.resultWinnerPercent}>
            {winnerPercent}%
          </strong>
        )}

        <span style={styles.resultPercentCaption}>
          {isEarlyResult
            ? t('voteRegistered', { count: safeTotalVotes })
            : hasWinner
              ? t('ofCommunity')
              : t('forEachSign')}
        </span>
      </div>

      {!isEarlyResult && (
        <div
          style={styles.resultProgressTrack}
          aria-hidden="true"
        >
          <div
            className="zdxResultProgressFill"
            style={{
              ...styles.resultProgressFill,
              width: `${Math.max(0, Math.min(100, winnerPercent))}%`,
            }}
          />
        </div>
      )}

      <div
        style={{
          ...styles.resultCommunityBadge,
          ...(matchedCommunity
            ? styles.resultCommunityBadgeHit
            : styles.resultCommunityBadgeMinority),
        }}
      >
        {isEarlyResult
          ? t('resultMayChange')
          : !hasWinner
            ? t('choiceTied', { percent: selectedPercent })
            : matchedCommunity
              ? t('matchedCommunity', { percent: selectedPercent })
              : selectedPercent !== null
                ? t('sharedReading', { percent: selectedPercent })
                : t('choiceIncluded')}
      </div>

      <div style={styles.resultNarrativeDivider} />

      <div style={styles.resultNarrativeTitle}>
        {hasWinner ? t('whyWon') : t('whatHappened')}
      </div>

      <div style={styles.resultNarrativeText}>
        {resultNarrative}
      </div>

      {predictionResult && (
        <div
          style={{
            ...styles.predictionResult,
            ...(predictionResult.hit
              ? styles.predictionResultHit
              : styles.predictionResultMiss),
          }}
        >
          {predictionResult.hit ? '🔮 ' : '🌙 '}
          {predictionResult.text}
        </div>
      )}
    </div>
  );
}
